/**
 * Applies the selected width (via slider, 10% steps) / float direction to the
 * clicked image and keeps the popover in sync with the current click target.
 */

import { Actions } from "../../constants/actions";
import { WysiwygForm } from "../wysiwyg-form";
import { WysiwygUtils } from "../wysiwyg-utils";
import {
    WysiwygImageToolbarRender,
    IMAGE_SIZE_MIN,
    IMAGE_SIZE_MAX,
    IMAGE_SIZE_CLASS_PREFIX,
    IMAGE_FLOAT_DIRECTIONS,
    IMAGE_FLOAT_CLASS_PREFIX,
} from "./wysiwyg-image-toolbar-render";

interface QuillLike {
    root: HTMLElement;
}

const LEGACY_SIZE_VALUES = ["25", "50", "75", "100"];

export class WysiwygImageToolbarHandler {
    private activeImage: HTMLImageElement | null = null;

    public constructor(
        private readonly quill: QuillLike,
        private readonly hostEl: HTMLElement,
        private readonly popover: HTMLElement,
        private readonly textarea: HTMLTextAreaElement,
    ) {}

    public handleEditorClick(event: MouseEvent): void {
        const target = event.target;
        if (target instanceof HTMLImageElement && this.quill.root.contains(target)) {
            this.activeImage = target;
            WysiwygImageToolbarRender.show(this.popover, this.hostEl, target);
            return;
        }
        this.hide();
    }

    public handleDocumentMouseDown(event: MouseEvent): void {
        const target = event.target;
        if (target instanceof Node && (this.popover.contains(target) || target === this.activeImage)) {
            return;
        }
        this.hide();
    }

    /** Live preview while dragging the size slider (no textarea sync, to avoid thrashing). */
    public handlePopoverInput(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.dataset.action !== Actions.SET_IMAGE_SIZE || !this.activeImage) {
            return;
        }

        const percent = this.clampSize(Number(target.value));
        this.applySize(percent);
        WysiwygImageToolbarRender.setSizeDisplay(this.popover, percent);
    }

    /** Slider release (or arrow-key step commit): apply and persist to the textarea. */
    public handlePopoverChange(event: Event): void {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.dataset.action !== Actions.SET_IMAGE_SIZE || !this.activeImage) {
            return;
        }

        const percent = this.clampSize(Number(target.value));
        this.applySize(percent);
        WysiwygImageToolbarRender.setSizeDisplay(this.popover, percent);
        this.syncTextarea();
    }

    public handlePopoverClick(event: MouseEvent): void {
        const button = WysiwygForm.getImageToolbarButton(event);
        if (!button || !this.activeImage) {
            return;
        }

        if (button.dataset.action === Actions.SET_IMAGE_FLOAT) {
            this.applyFloat(String(button.dataset.value || ""));
            this.syncTextarea();
        }
    }

    private clampSize(value: number): number {
        if (!Number.isFinite(value)) return IMAGE_SIZE_MIN;
        return Math.min(IMAGE_SIZE_MAX, Math.max(IMAGE_SIZE_MIN, value));
    }

    private applySize(percent: number): void {
        if (!this.activeImage) return;
        LEGACY_SIZE_VALUES.forEach((size) => this.activeImage?.classList.remove(`${IMAGE_SIZE_CLASS_PREFIX}${size}`));
        this.activeImage.style.width = `${percent}%`;
        this.activeImage.style.maxWidth = `${percent}%`;
    }

    private applyFloat(value: string): void {
        if (!this.activeImage) return;
        IMAGE_FLOAT_DIRECTIONS.forEach((dir) => this.activeImage?.classList.remove(`${IMAGE_FLOAT_CLASS_PREFIX}${dir}`));
        if ((IMAGE_FLOAT_DIRECTIONS as readonly string[]).includes(value)) {
            this.activeImage.classList.add(`${IMAGE_FLOAT_CLASS_PREFIX}${value}`);
        }
    }

    private syncTextarea(): void {
        this.textarea.value = this.quill.root.innerHTML;
        WysiwygUtils.dispatchTextareaEvent(this.textarea, "input");
    }

    private hide(): void {
        this.activeImage = null;
        WysiwygImageToolbarRender.hide(this.popover);
    }
}
